import { createRoute, Outlet, redirect, Link } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import { Settings, LogOut, Menu, X } from 'lucide-react';
import { rootRoute } from './__root';
import { useAuth } from '@/hooks/useAuth';
import { usePWA } from '@/hooks/usePWA';
import { InstallBanner } from '@/components/pwa/InstallBanner';
import { OfflineBanner } from '@/components/OfflineBanner';
import { ThemeToggle } from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';
import { isAuthenticated } from '@/lib/authGuard';

export const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authenticated',
  beforeLoad: async ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.pathname,
        },
      });
    }
  },
  component: AuthenticatedLayout,
});

// Backward compatibility
export const Route = authenticatedRoute;

function AuthenticatedLayout() {
  const { user, isLoading, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { shouldShowBanner, install, dismissBanner } = usePWA();

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await logout();
    window.location.href = '/';
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div data-testid="authenticated-layout" className="min-h-screen flex flex-col">
      {/* Offline Banner - fixed at top when offline */}
      <OfflineBanner />

      <nav className="border-b bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Left: Mobile Menu Button + Navigation Links */}
            <div className="flex items-center gap-2 md:gap-6">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>

              {/* Desktop Navigation Links */}
              <div className="hidden md:flex items-center gap-6">
                <Link
                  to="/gallery"
                  className="text-sm font-medium transition-colors hover:text-primary"
                  activeProps={{ className: 'text-primary' }}
                >
                  Gallery
                </Link>
                <Link
                  to="/albums"
                  className="text-sm font-medium transition-colors hover:text-primary"
                  activeProps={{ className: 'text-primary' }}
                >
                  Albums
                </Link>
                <Link
                  to="/about"
                  className="text-sm font-medium transition-colors hover:text-primary"
                  activeProps={{ className: 'text-primary' }}
                >
                  About
                </Link>
                <Link
                  to="/download"
                  className="text-sm font-medium transition-colors hover:text-primary"
                  activeProps={{ className: 'text-primary' }}
                >
                  Download
                </Link>
              </div>
            </div>

            {/* Right: Theme Toggle + User Menu with Dropdown */}
            <div className="flex items-center gap-4">
              <ThemeToggle />
              {isLoading ? (
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
                  <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                </div>
              ) : user ? (
                <div ref={dropdownRef} className="relative">
                  {/* User Menu Button */}
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    aria-haspopup="true"
                    aria-expanded={isDropdownOpen}
                    aria-label="User menu"
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg',
                      'hover:bg-muted transition-colors',
                      'cursor-pointer',
                      'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
                    )}
                  >
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={user.displayName || 'User'}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {user.displayName?.charAt(0).toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                    <span className="text-sm font-medium hidden sm:inline-block">
                      {user.displayName || 'User'}
                    </span>
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div
                      role="menu"
                      aria-orientation="vertical"
                      className={cn(
                        'absolute right-0 top-full mt-2',
                        'w-48 rounded-lg',
                        'bg-background',
                        'border border-border',
                        'shadow-lg',
                        'py-1',
                        'z-50'
                      )}
                    >
                      <Link
                        to="/settings"
                        role="menuitem"
                        onClick={() => setIsDropdownOpen(false)}
                        className={cn(
                          'w-full flex items-center gap-2 px-4 py-2',
                          'text-sm text-foreground',
                          'hover:bg-muted',
                          'transition-colors'
                        )}
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                      <button
                        role="menuitem"
                        onClick={handleLogout}
                        className={cn(
                          'w-full flex items-center gap-2 px-4 py-2',
                          'text-left text-sm text-foreground',
                          'hover:bg-muted',
                          'transition-colors'
                        )}
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border">
            <div className="flex flex-col py-2">
              <Link
                to="/gallery"
                className="px-4 py-3 text-sm font-medium transition-colors hover:bg-muted"
                activeProps={{ className: 'text-primary bg-muted' }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Gallery
              </Link>
              <Link
                to="/albums"
                className="px-4 py-3 text-sm font-medium transition-colors hover:bg-muted"
                activeProps={{ className: 'text-primary bg-muted' }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Albums
              </Link>
              <Link
                to="/about"
                className="px-4 py-3 text-sm font-medium transition-colors hover:bg-muted"
                activeProps={{ className: 'text-primary bg-muted' }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About
              </Link>
              <Link
                to="/download"
                className="px-4 py-3 text-sm font-medium transition-colors hover:bg-muted"
                activeProps={{ className: 'text-primary bg-muted' }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Download
              </Link>
            </div>
          </div>
        )}
      </nav>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* PWA Install Banner - shows at bottom when conditions are met */}
      {shouldShowBanner && <InstallBanner onInstall={install} onDismiss={dismissBanner} />}
    </div>
  );
}
